import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";

const empty = { question: "", answer: "", order: 0 };

export default function AdminFAQs() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        setItems((await api.get("/faqs")).data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this FAQ?")) return;
        await api.delete(`/admin/faqs/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid="admin-faqs-page">
            <Header
                title="FAQ"
                subtitle="Pertanyaan & jawaban yang sering ditanya client. Tampil sebagai accordion di section 'Frequently asked'."
                action={
                    <button onClick={() => setEditing({ ...empty })} data-testid="faq-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover">
                        <Plus size={16} /> New FAQ
                    </button>
                }
            />
            {loading ? <div className="mono-label">Loading…</div> : (
                <div className="space-y-3">
                    {items.map((f, i) => (
                        <div key={f.id} className="p-5 rounded-xl border border-white/5 bg-ink-800/40 flex items-start gap-4">
                            <div className="font-mono text-ink-300 text-xs pt-1 flex-shrink-0">{String(i + 1).padStart(2, "0")}</div>
                            <div className="min-w-0 flex-1">
                                <div className="text-white font-medium">{f.question}</div>
                                <p className="mt-1.5 text-sm text-ink-200 line-clamp-2">{f.answer}</p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => setEditing({ ...f })} className="p-2 rounded hover:bg-white/10 text-ink-200 hover:text-white"><Pencil size={14} /></button>
                                <button onClick={() => remove(f.id)} className="p-2 rounded hover:bg-red-500/10 text-ink-200 hover:text-red-400"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && <div className="p-12 text-center text-ink-300 border border-dashed border-white/10 rounded-xl">No FAQs yet.</div>}
                </div>
            )}
            {editing && <Editor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function Editor({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const u = (k) => (e) => setData((s) => ({ ...s, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) await api.put(`/admin/faqs/${initial.id}`, data);
            else await api.post("/admin/faqs", data);
            toast.success(isEdit ? "Updated" : "Created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur" data-testid="faq-editor">
            <div className="w-full max-w-xl bg-ink-900 border-l border-white/10 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="mono-label">{isEdit ? "Edit FAQ" : "New FAQ"}</div>
                        <h2 className="font-display text-xl text-white truncate max-w-md">{data.question || "Untitled"}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} disabled={saving} data-testid="faq-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10 text-ink-200"><X size={16} /></button>
                    </div>
                </div>
                <div className="p-6 space-y-5">
                    <Card title="FAQ entry">
                        <Field label="Question"><input className="ainp" value={data.question} onChange={u("question")} /></Field>
                        <Field label="Answer"><textarea rows={6} className="ainp" value={data.answer} onChange={u("answer")} /></Field>
                        <Field label="Order"><input type="number" className="ainp" value={data.order} onChange={u("order")} /></Field>
                    </Card>
                </div>
            </div>
        </div>
    );
}
